import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, Share2, BookmarkMinus } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveBook } from '@/features/library/api/library';

interface BookActionsMenuProps {
  bookId: number;
  title: string;
  author: string;
  isSaved?: boolean;
  className?: string;
  triggerClassName?: string;
  iconClassName?: string;
}

const BookActionsMenu: React.FC<BookActionsMenuProps> = ({ bookId, title, author, isSaved, className, triggerClassName, iconClassName }) => {
  const [open, setOpen] = useState(false);
  const [positionAbove, setPositionAbove] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left?: number; right?: number }>({ top: 0, right: 0 });
  const qc = useQueryClient();

  const unsaveMut = useMutation({
    mutationFn: () => saveBook(bookId),
    onSuccess: () => {
      qc.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && String(q.queryKey[0]).startsWith('userLibrary') });
      qc.invalidateQueries({ queryKey: ['book', bookId] });
      setOpen(false);
    }
  });

  // Calculate menu position using viewport coords and render via portal to avoid clipping
  useEffect(() => {
    if (!open) return;

    const calc = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Default to below with 8px gap
      let top = rect.bottom + 8;
      let above = false;

      const menuH = menuRef.current?.offsetHeight || 0;
      const menuW = menuRef.current?.offsetWidth || 180;
      if (menuH && spaceBelow < menuH + 20 && spaceAbove > spaceBelow) {
        top = rect.top - menuH - 8;
        above = true;
      }

      setPositionAbove(above);
      const padding = 8;
      const leftCandidate = rect.right - menuW; // if we anchor by right, left position becomes this

      if (leftCandidate >= padding) {
        // Anchor by right edge of trigger
        setCoords({ top, right: viewportWidth - rect.right });
      } else {
        // Anchor by left, clamped to viewport
        const left = Math.max(padding, Math.min(rect.left, viewportWidth - menuW - padding));
        setCoords({ top, left });
      }
    };

    // Initial calculation and listeners to keep it in place while scrolling/resizing
    calc();
    // In case menu size is 0 on first paint, recalc on next frame
    const raf = requestAnimationFrame(calc);
    const onScroll = () => calc();
    const onResize = () => calc();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(raf);
    };
  }, [open]);

  const onShare = async () => {
    const url = `${window.location.origin}/library/book/${bookId}`;
    const data = { title, text: `${title} - ${author}`, url } as any;
    try {
      const navAny = (window as any).navigator;
      if (navAny && 'share' in navAny && typeof navAny.share === 'function') {
        await navAny.share(data);
      } else if (navAny?.clipboard?.writeText) {
        await navAny.clipboard.writeText(url);
      }
    } catch {}
    setOpen(false);
  };

  return (
    <div className={cn('relative', className)} onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        aria-label="more"
        className={cn('p-2 rounded-lg hover:bg-black/5 active:scale-95 transition', triggerClassName)}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      >
        <MoreVertical className={cn('w-4 h-4', iconClassName || 'text-white')} />
      </button>
      {open && (
        createPortal(
          <>
            <div className="fixed inset-0 z-[999]" onMouseDown={(e) => { e.stopPropagation(); setOpen(false); }} />
            <div
              ref={menuRef}
              className={cn(
                'fixed z-[1000] min-w-[160px] rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden',
                positionAbove ? '' : ''
              )}
              style={{ top: coords.top, right: coords.right, left: coords.left }}
            >
              <button
                className="w-full flex items-center gap-2 px-4 py-3 text-right text-sm font-persian hover:bg-gray-50"
                onClick={onShare}
              >
                <Share2 className="w-4 h-4 text-gray-600" />
                <span>اشتراک‌گذاری</span>
              </button>
              {isSaved && (
                <button
                  className="w-full flex items-center gap-2 px-4 py-3 text-right text-sm font-persian text-red-600 hover:bg-red-50"
                  onClick={() => unsaveMut.mutate()}
                >
                  <BookmarkMinus className="w-4 h-4" />
                  <span>حذف از ذخیره‌ها</span>
                </button>
              )}
            </div>
          </>,
          document.body
        )
      )}
    </div>
  );
};

export default BookActionsMenu;
